import { FontSizes, FontWeights } from '../../../../../types/common/types';
import { getPagedNews } from '../../rssFeedParser';
import Glide from '@glidejs/glide';
import { LatestNewsCard } from '../../types';
import { LitElement, html, TemplateResult, PropertyValues } from 'lit';
import { property, customElement, state, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { translateFn } from '../../../../../utils/translator.helper';
import { GzLandingPageCompanyType } from '../../../../../landingPage/types';

/**
 * @summary Type here the description of this component
 * @tag gz-latest-news-carousel-widget
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('gz-latest-news-carousel-widget')
export class LatestNewsCarouselWebComponent extends LitElement {
  private glide: Glide;

  @state()
  private activeSlideIndex = 0;

  @property({ type: Number })
  companyType: GzLandingPageCompanyType;

  @property({ type: Boolean, reflect: true })
  isLoading = false;

  @property({ type: Array, reflect: true })
  pagedNews: LatestNewsCard[][] = [];

  @query('.glide')
  glideContainer: HTMLElement;

  @property({ type: Array, reflect: true })
  rssFeedUrls: string[] = [];

  private resizeObserver: ResizeObserver;

  static get styles() {
    return styles;
  }

  updated(_changedProperties: PropertyValues): void {
    if (_changedProperties.has('pagedNews')) {
      this.initSlider();
    }
  }

  connectedCallback() {
    super.connectedCallback();

    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(this);
  }

  disconnectedCallback(): void {
    if (this.glide) {
      this.glide.destroy();
    }

    this.resizeObserver.unobserve(this);

    super.disconnectedCallback();
  }

  private initSlider(): void {
    const previousIndex = this.glide?.index ?? 0;
    // Destroy the existing Glide instance if it exists
    if (this.glide) {
      this.glide.destroy();
    }

    if (!this.glideContainer) {
      return;
    }

    // Initialize Glide with the provided options
    // See https://glidejs.com/docs/ for the complete documentation
    this.glide = new Glide(this.glideContainer, {
      type: 'carousel',
      perView: 1,
      startAt: previousIndex,
      focusAt: 'center',
      pagination: {
        el: '.glide__pagination',
        clickable: true,
      },
    });

    this.glide.mount();

    this.glide.on(['run'], () => {
      this.activeSlideIndex = this.glide.index;
    });
  }

  private handleResize(): void {
    this.initSlider();
  }

  private setPagedNews(): void {
    this.isLoading = true;

    getPagedNews(this.rssFeedUrls)
      .then(news => {
        this.pagedNews = news;
        this.isLoading = false;
      })
      .catch(err => {
        console.error(err);
      });
  }

  private renderSlider(): TemplateResult {
    return html`
      <div class="glide">
        <gz-title
          id="latest-news-title"
          class="slide-title"
          fontWeight=${FontWeights.MEDIUM}
          fontSize=${FontSizes.HEADING_3}
        >
          ${translateFn('Latest News')}
        </gz-title>
        ${this.renderPagination(this.pagedNews.length)}
        <div class="glide__track" data-glide-el="track">
          <div class="glide__slides">${this.renderSlides(this.pagedNews)}</div>
        </div>
      </div>
    `;
  }

  private renderPagination(pageCount: number): TemplateResult | undefined {
    if (pageCount === 0) {
      return;
    }

    return html`
      <div class="glide__pagination" data-glide-el="controls[nav]">
        ${this.pagedNews.map((_page: LatestNewsCard[], index) => {
          return html`
            <gz-button
              class="glide-nav-button"
              data-glide-dir="=${index}"
              type="${this.activeSlideIndex === index ? 'full' : 'outline'}"
              buttonHeight="small"
              buttonFontSize="small"
              color="${this.activeSlideIndex === index ? 'primary' : 'secondary'}"
              >${index + 1}
            </gz-button>
          `;
        })}
      </div>
    `;
  }

  private renderNewsCard(item: LatestNewsCard, index: number): TemplateResult {
    const { postTitle, tag, summary, postUrl, imageUrl, id } = item;
    const cardColor = tag === 'Announcement' ? 'primary' : 'white';
    return html`<gz-latest-news-card-widget
      .id="${id}"
      .postTitle="${postTitle}"
      .tag="${tag}"
      .summary="${summary}"
      .imageUrl="${imageUrl}"
      .cardColor="${cardColor}"
      .postUrl="${postUrl}"
      .index="${index}"
      .companyType="${this.companyType}"
    ></gz-latest-news-card-widget>`;
  }

  private renderSlides(slides: LatestNewsCard[][]): TemplateResult {
    return html`${slides.map((slide: LatestNewsCard[]) => {
      return html`<div class="glide__slide">
        <div class="slide-content">${slide.map((item: LatestNewsCard, index) => this.renderNewsCard(item, index))}</div>
      </div>`;
    })}`;
  }

  private renderSkeleton(): TemplateResult {
    return html`
      <div class="loading-news" style="grid-area: news">
        <gz-text fontWeight=${FontWeights.SEMIBOLD} fontSize=${FontSizes.HEADING_3}>
          ${translateFn('Latest News')}
        </gz-text>
        <div class="group">
          ${[...Array(3).keys()].map(
            () =>
              html`<div class="card news-card">
                <div class="image-placeholder"></div>
                <div class="news-footer">
                  <gz-loader rows="2"></gz-loader>
                </div>
              </div>`
          )}
        </div>
      </div>
    `;
  }

  render() {
    if (!this.pagedNews || this.pagedNews.length === 0) {
      this.setPagedNews();
    }

    if (this.isLoading) {
      return this.renderSkeleton();
    }

    return html`${this.renderSlider()}`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-latest-news-carousel-widget': LatestNewsCarouselWebComponent;
  }
}
